import { useState } from 'react';
import { AdminView } from './views/AdminView';
import { ProjectorView } from './views/ProjectorView';
import { TeamView } from './views/TeamView';
import { Icon } from './components/Icon';
import type { TeamId } from './types/game';

type Route =
  | { view: 'home' }
  | { view: 'admin'; gameId: string }
  | { view: 'projector'; gameId: string }
  | { view: 'team'; gameId: string; team: TeamId };

function parseRoute(): Route {
  const hash = window.location.hash.replace(/^#+\/?/, '');
  const parts = hash.split('/').filter(Boolean);

  if (parts[0] === 'admin' && parts[1]) return { view: 'admin', gameId: parts[1] };
  if (parts[0] === 'projector' && parts[1]) return { view: 'projector', gameId: parts[1] };
  if (parts[0] === 'team' && parts[1] && parts[2]) {
    const team = parts[2].toUpperCase() as TeamId;
    if (['A', 'B', 'C'].includes(team)) return { view: 'team', gameId: parts[1], team };
  }
  return { view: 'home' };
}

function navigate(path: string) {
  window.location.hash = path;
  window.location.reload();
}

function HomeView() {
  const [gameId, setGameId] = useState('');
  const [creating, setCreating] = useState(false);

  const createGame = async () => {
    setCreating(true);
    try {
      const API = import.meta.env.VITE_API_URL ?? '/api';
      console.log('Creating game via:', `${API}/games`);
      const res = await fetch(`${API}/games`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grade: '1' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { gameId: string };
      if (!data.gameId) throw new Error('Sin gameId en respuesta');
      navigate(`admin/${data.gameId}`);
    } catch (e) {
      alert(`Error al crear la partida: ${e}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="home bg-grid-tech">
      <div className="home__hero">
        <div className="home__icon"><Icon name="trophy" size="7xl" /></div>
        <h1 className="home__title">Concurso Académico</h1>
        <p className="home__subtitle">Plataforma en tiempo real para concursos escolares</p>
      </div>

      <div className="home__cards">
        <div className="home__card">
          <h2>Administrar partida</h2>
          <p>Crea una nueva partida y obtén un ID para compartir.</p>
          <button className="btn btn-primary btn-lg" onClick={createGame} disabled={creating}>
            {creating ? 'Creando…' : '+ Crear nueva partida'}
          </button>
        </div>

        <div className="home__card">
          <h2>Unirse a partida</h2>
          <p>Ingresa el ID de partida y elige tu rol.</p>
          <input
            className="home__input"
            placeholder="ID de partida (ej: AB12CD34)"
            value={gameId}
            onChange={e => setGameId(e.target.value.toUpperCase())}
            maxLength={8}
          />
          <div className="home__join-btns">
            <button className="btn btn-outline" disabled={!gameId}
              onClick={() => navigate(`projector/${gameId}`)}>
              <Icon name="tv" /> Proyector
            </button>
            {(['A', 'B', 'C'] as TeamId[]).map(t => (
              <button key={t} className="btn btn-outline" disabled={!gameId}
                onClick={() => navigate(`team/${gameId}/${t}`)}>
                Equipo {t}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const route = parseRoute();
  switch (route.view) {
    case 'admin': return <AdminView gameId={route.gameId} />;
    case 'projector': return <ProjectorView gameId={route.gameId} />;
    case 'team': return <TeamView gameId={route.gameId} team={route.team} />;
    default: return <HomeView />;
  }
}